import type { ContactPoint, ContactPointRow } from './contactPoints';
import { emptyContactPointRow, rowsToContactPoints } from './contactPoints';

export type OrganizationContactRow = {
  key: string;
  /** Persisted on email contact points so split contacts stay separate after save. */
  groupKey: string;
  contactName: string;
  contactPosition: string;
  emails: ContactPointRow[];
};

let contactKeyCounter = 0;
let groupKeyCounter = 0;

function nextContactKey(): string {
  contactKeyCounter += 1;
  return `contact-${contactKeyCounter}`;
}

function nextGroupKey(): string {
  groupKeyCounter += 1;
  return `org-contact-group-${groupKeyCounter}-${Date.now()}`;
}

export function emptyOrganizationContact(_markPrimary = false): OrganizationContactRow {
  return {
    key: nextContactKey(),
    groupKey: nextGroupKey(),
    contactName: '',
    contactPosition: '',
    emails: [emptyContactPointRow('EMAIL', true)],
  };
}

/** Single-email contacts are always primary; multi-email keeps one primary. */
export function normalizeOrganizationContactEmails(emails: ContactPointRow[]): ContactPointRow[] {
  if (emails.length === 0) return emails;
  if (emails.length === 1) {
    return [{ ...emails[0], isPrimary: true }];
  }

  const hasPrimary = emails.some((row) => row.isPrimary);
  let primaryAssigned = false;
  return emails.map((row, index) => {
    const shouldBePrimary = hasPrimary ? row.isPrimary && !primaryAssigned : index === 0;
    if (shouldBePrimary) primaryAssigned = true;
    return { ...row, isPrimary: shouldBePrimary };
  });
}

export function normalizeOrganizationContact(contact: OrganizationContactRow): OrganizationContactRow {
  return {
    ...contact,
    emails: normalizeOrganizationContactEmails(contact.emails),
  };
}

function contactPointToRow(point: ContactPoint): ContactPointRow {
  return {
    id: point.id,
    value: point.value,
    label: point.label || 'Work',
    isPrimary: Boolean(point.isPrimary),
  };
}

function resolvePointGroupKey(point: ContactPoint): string {
  const persisted = point.contactGroupKey?.trim();
  if (persisted) return persisted;
  // Legacy rows without a group key: keep one email per contact card.
  return `legacy-${point.id ?? `${point.sortOrder ?? 0}-${point.value.trim()}`}`;
}

export function groupContactPointsToOrganizationContacts(
  points?: ContactPoint[],
): OrganizationContactRow[] {
  const sorted = [...(points ?? [])]
    .filter((point) => point.kind === 'EMAIL' && point.value?.trim())
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  if (sorted.length === 0) {
    return [emptyOrganizationContact(true)];
  }

  const groupMap = new Map<string, { row: OrganizationContactRow; minOrder: number }>();

  for (const point of sorted) {
    const groupKey = resolvePointGroupKey(point);

    if (!groupMap.has(groupKey)) {
      groupMap.set(groupKey, {
        minOrder: point.sortOrder ?? 0,
        row: {
          key: nextContactKey(),
          groupKey: point.contactGroupKey?.trim() || groupKey,
          contactName: point.contactName?.trim() ?? '',
          contactPosition: point.contactPosition?.trim() ?? '',
          emails: [],
        },
      });
    }

    const entry = groupMap.get(groupKey)!;
    entry.minOrder = Math.min(entry.minOrder, point.sortOrder ?? 0);
    entry.row.emails.push(contactPointToRow(point));
  }

  return Array.from(groupMap.values())
    .sort((a, b) => a.minOrder - b.minOrder)
    .map(({ row }, index) =>
      normalizeOrganizationContact({
        ...row,
        emails: row.emails.length > 0 ? row.emails : [emptyContactPointRow('EMAIL', true)],
      }),
    );
}

export function flattenOrganizationContacts(contacts?: OrganizationContactRow[]): ContactPoint[] {
  const flattened: ContactPoint[] = [];

  for (const contact of contacts ?? []) {
    const person = {
      contactName: contact.contactName.trim() || undefined,
      contactPosition: contact.contactPosition.trim() || undefined,
    };
    const groupKey = contact.groupKey?.trim() || nextGroupKey();
    const emails = rowsToContactPoints(contact.emails, 'EMAIL', person).map((point) => ({
      ...point,
      contactGroupKey: groupKey,
    }));
    if (emails.length === 0 && !person.contactName && !person.contactPosition) {
      continue;
    }
    flattened.push(...emails);
  }

  return flattened.map((point, index) => ({ ...point, sortOrder: index }));
}

export function countFilledEmails(contact: OrganizationContactRow): number {
  return contact.emails.filter((row) => row.value.trim()).length;
}

export function hasSplittableOrganizationContacts(contacts: OrganizationContactRow[]): boolean {
  return contacts.some((contact) => countFilledEmails(contact) > 1);
}

/** One contact with multiple emails becomes one contact per email (same name/position). */
export function splitOrganizationContact(contact: OrganizationContactRow): OrganizationContactRow[] {
  const emails = contact.emails.filter((row) => row.value.trim());
  if (emails.length <= 1) return [normalizeOrganizationContact(contact)];

  return emails.map((email) => ({
    key: nextContactKey(),
    groupKey: nextGroupKey(),
    contactName: contact.contactName,
    contactPosition: contact.contactPosition,
    emails: [{ ...email, isPrimary: true }],
  }));
}

export function splitOrganizationContactsByEmail(
  contacts: OrganizationContactRow[],
): OrganizationContactRow[] {
  const split = contacts.flatMap(splitOrganizationContact);
  return split.length > 0
    ? split.map(normalizeOrganizationContact)
    : [emptyOrganizationContact(true)];
}
